import React, {
    ChangeEvent,
    useEffect,
    useState,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import {
    setTicketsListMountStatus,
    fetchTotalNumberOfTicketsOpenAssignedToLoggedInUser,
} from 'flow/actions/tickets-action';
import { selectExternalContactsExistsInStore } from 'flow/selectors/contact-selectors';
import { selectTicketPageMounted } from 'flow/selectors/TicketsSelectors';
import { TICKETS_LIST_TABS_KEYS } from 'flow/utils/ticket-utils';
import { TicketKey } from 'flow/types/EntityKeys';
import { AdvancedSearchFilters, TicketsSearchParams } from 'flow/types/Tickets';
import { fetchPersonalAndSharedContacts } from 'flow/actions/contact-actions';

import TicketsListMineWrapper from 'flow/components/tickets/tickets-list/TicketsListMineWrapper';
import TicketsListOpenUnassignedWrapper from 'flow/components/tickets/tickets-list/TicketsListOpenUnassignedWrapper';
import TicketsListAllWrapper from 'flow/components/tickets/tickets-list/TicketsListAllWrapper';
import TicketsListSearchResultWrapper from 'flow/components/tickets/tickets-list/TicketsListSearchResultWrapper';

import TicketsSearchInputSection from 'flow/components/tickets/convenience/search/TicketsSearchInputSection';
import { hasAnyAdvancedFilterActive } from 'flow/components/tickets/convenience/search/filter-utils';
import TicketsTabs, {
    TabsType,
} from 'flow/components/tickets/tickets-list/TicketsTabs';
import { tabV2Id } from 'flow/components/pbx/v2/misc/TabsV2';

const EMPTY_ADVANCED_FILTERS: AdvancedSearchFilters = {
    marked: false,
    since: '',
};

const TicketsListsSection = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const [searchQuery, setSearchQuery] = useState('');
    const [advancedFilters, setAdvancedFilters] =
        useState<AdvancedSearchFilters>(EMPTY_ADVANCED_FILTERS);

    const ticketSelectedKey = useAppSelector(selectTicketPageMounted);

    const externalContactsFoundInStore = useAppSelector(
        selectExternalContactsExistsInStore,
    );

    const listParentRef = useRef(null);
    const [tabSelected, setTabSelected] = useState<TabsType>('MY_TICKETS');
    // The last non-Filter tab the user selected. Restored when the temporary
    // "Filter" pill is dismissed, so clearing filters returns the user to the
    // tab they had chosen rather than always snapping back to "All".
    const lastNonFilterTabRef = useRef<TabsType>('MY_TICKETS');
    // Whether the temporary "Filter" pill is currently shown. Turned on when
    // the user applies an advanced filter, off when filters are reset.
    const [filterTabActive, setFilterTabActive] = useState(false);

    const [scrollableTargetId, setScrollableTargetId] = useState(
        `${tabSelected}-list`,
    );

    useEffect(() => {
        return () => {
            dispatch(setTicketsListMountStatus(false, undefined));
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(setTicketsListMountStatus(true, tabSelected));
    }, [dispatch, tabSelected]);

    useEffect(() => {
        setScrollableTargetId(`${tabSelected}-list`);
        if (listParentRef?.current) {
            listParentRef.current.scrollTop = 0;
        }
    }, [listParentRef, tabSelected]);

    useEffect(() => {
        if (tabSelected === TICKETS_LIST_TABS_KEYS.MY_TICKETS) {
            dispatch(fetchTotalNumberOfTicketsOpenAssignedToLoggedInUser);
            // Above fetch should not be necessary, given that we act on Top events related to tickets appropriately
            // Leaving it here for now, to minimize the risk of the count not being correct
        }
    }, [dispatch, tabSelected]);

    useEffect(() => {
        if (!externalContactsFoundInStore) {
            dispatch(fetchPersonalAndSharedContacts());
        }
    }, [dispatch, externalContactsFoundInStore]);

    const debouncedSearchQueryHandler = useMemo(
        () => debounce(setSearchQuery, 500),
        [],
    );

    const searchQueryHandler = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            if (inputValue) {
                debouncedSearchQueryHandler(inputValue);
            } else {
                setSearchQuery(inputValue);
            }
        },
        [debouncedSearchQueryHandler],
    );

    // Applying non-empty filters (re)opens the temporary "Filter" pill with
    // those filters and selects it - from any pill, so a new filter search
    // started elsewhere replaces the existing temporary pill. Resetting to
    // empty only matters while on the Filter pill, where it dismisses it.
    const handleFiltersApply = useCallback(
        (filters: AdvancedSearchFilters) => {
            if (hasAnyAdvancedFilterActive(filters)) {
                setAdvancedFilters(filters);
                setFilterTabActive(true);
                setTabSelected('FILTER');
            } else if (tabSelected === 'FILTER') {
                setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
                setFilterTabActive(false);
                setTabSelected(lastNonFilterTabRef.current);
            }
        },
        [tabSelected],
    );

    const ticketsSearchParams = useMemo((): TicketsSearchParams => {
        const params: TicketsSearchParams = { searchQuery };
        if (advancedFilters.state) params.state = advancedFilters.state;
        if (advancedFilters.marked) params.marked = advancedFilters.marked;
        if (advancedFilters.since) {
            const timestamp = new Date(advancedFilters.since).getTime();
            if (!Number.isNaN(timestamp)) params.since = timestamp;
        }
        if (advancedFilters.assignee) {
            params.assignee = advancedFilters.assignee;
        }
        if (advancedFilters.tagKeysOr && advancedFilters.tagKeysOr.length > 0) {
            params.tagKeysOr = advancedFilters.tagKeysOr;
        }
        return params;
    }, [searchQuery, advancedFilters]);

    // Plain text search on the normal pills must NOT carry the persisted
    // Filter-pill filters - only the query itself (the pill's own context
    // adds its assignee/state scoping server-side).
    const textSearchParams = useMemo<TicketsSearchParams>(
        () => ({ searchQuery }),
        [searchQuery],
    );

    // The temporary "Filter" pill persists across pill navigation; switching
    // pills just changes the view (the popup is emptied on non-Filter pills
    // via `popupFilters` below).
    const onTabClick = useCallback((key: TabsType) => {
        // Remember the user's last real tab so dismissing the Filter pill can
        // return here; the Filter pill itself is not a destination to restore.
        if (key !== 'FILTER') lastNonFilterTabRef.current = key;
        setTabSelected(key);
    }, []);

    const onClickListItem = useCallback(
        (key: TicketKey) =>
            history.push(
                generatePath(routes['tickets.ticketKey'], { ticketKey: key }),
            ),
        [history],
    );

    // The popup shows the committed filters only while the Filter pill is
    // selected; on every other pill it opens empty so the user starts fresh.
    const popupFilters =
        tabSelected === 'FILTER' ? advancedFilters : EMPTY_ADVANCED_FILTERS;
    const hasActiveAdvancedFilters = hasAnyAdvancedFilterActive(popupFilters);

    const getListComponent = () => {
        // The temporary "Filter" pill hosts the advanced-search results for
        // the applied filters (combined with any text in the search box).
        if (tabSelected === 'FILTER') {
            return (
                <TicketsListSearchResultWrapper
                    listItemClicked={onClickListItem}
                    itemSelectedKey={ticketSelectedKey}
                    scrollableTargetId={scrollableTargetId}
                    ticketsSearchParams={ticketsSearchParams}
                    context={'ADVANCED_SEARCH'}
                />
            );
        }

        // Free-text search stays scoped to the currently selected pill and
        // ignores any persisted Filter-pill filters.
        if (searchQuery.length > 2) {
            return (
                <TicketsListSearchResultWrapper
                    listItemClicked={onClickListItem}
                    itemSelectedKey={ticketSelectedKey}
                    scrollableTargetId={scrollableTargetId}
                    ticketsSearchParams={textSearchParams}
                    context={tabSelected}
                />
            );
        }

        switch (tabSelected) {
            case TICKETS_LIST_TABS_KEYS.MY_TICKETS:
                return (
                    <TicketsListMineWrapper
                        listItemClicked={onClickListItem}
                        itemSelectedKey={ticketSelectedKey}
                        scrollableTargetId={scrollableTargetId}
                    />
                );
            case TICKETS_LIST_TABS_KEYS.UNASSIGNED:
                return (
                    <TicketsListOpenUnassignedWrapper
                        listItemClicked={onClickListItem}
                        itemSelectedKey={ticketSelectedKey}
                        scrollableTargetId={scrollableTargetId}
                    />
                );
            case TICKETS_LIST_TABS_KEYS.ALL:
                return (
                    <TicketsListAllWrapper
                        listItemClicked={onClickListItem}
                        itemSelectedKey={ticketSelectedKey}
                        scrollableTargetId={scrollableTargetId}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="scrollbar flex grow flex-col border-e border-solid">
            <div className="pt-3">
                <TicketsSearchInputSection
                    onSearchQueryChange={searchQueryHandler}
                    onFiltersApply={handleFiltersApply}
                    appliedAdvancedSearchFilters={popupFilters}
                    hasActiveAdvancedFilters={hasActiveAdvancedFilters}
                />
            </div>
            <div className="py-3">
                <TicketsTabs
                    onClick={onTabClick}
                    selectedTab={tabSelected}
                    showFilterTab={filterTabActive}
                    context={'tickets lists section'}
                    panelId={scrollableTargetId}
                />
            </div>
            <div
                className="scrollbar scrollbar-gutter-stable ps-3 pe-1 pb-3"
                id={scrollableTargetId}
                ref={listParentRef}
                role="tabpanel"
                aria-labelledby={tabV2Id(scrollableTargetId, tabSelected)}
            >
                {getListComponent()}
            </div>
        </div>
    );
};

export default React.memo(TicketsListsSection);
